import React, { useCallback, useEffect, useRef } from 'react';
import styles from './Notifications.module.scss';
import { ScreenComponent } from 'new/routes/RouterManager';
import { ROUTE_REDESIGN } from 'js/constants/pathnames';
import { ROUTE_MONITOR } from 'new/constants/pathnames';
import { TextField } from 'new/elements/TextField';
import { Button } from 'new/elements/Button';
import { SearchRounded } from '@mui/icons-material';
import { ReactComponent as ArrowSmallRight } from 'new/components/icons/fi-rr-arrow-small-right.svg';
import { useNotifications } from 'new/hooks/adapters/useNotifications';
import { useDispatch } from 'react-redux';
import { loadAllNotifications, setContextualSupport, unsetContextualSupport } from 'js/actions';
import * as notificationsTypes from 'js/constants/notificationsTypes';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';

import { GET_ALL_NOTIFICATIONS_REQUEST } from 'js/constants/actionTypes';
import { useFilteredData } from 'new/hooks/useFilteredData';
import { NotificationsListView } from 'new/elements/Notifications/NotificationsListView';
import { useHistory } from 'react-router-dom';
import { NotificationSettings } from 'new/components/monitor/NotificationSettings';
import { BannerDescription } from 'new/elements/BannerDescription';
import { SUPPORT_KEY_NOTIFICATION } from 'js/constants/supportPageKeys';
import useURLSearchParams from 'new/hooks/useURLSearchParams';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';

interface NotificationsProps {
  resourceType: string;
  resourceId: string;
}

const BATCH_SIZE = 50;
const description = 'Detailed monitoring information across all of your resources.';

export const Notifications: ScreenComponent<NotificationsProps> = () => {
  const headerRef = useRef();
  const history = useHistory();
  const loading = useIsLoading(GET_ALL_NOTIFICATIONS_REQUEST);
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const { notifications: items, meta } = useNotifications();
  const { params, updateUrl } = useURLSearchParams('page');
  const { filteredData: notifications, onSearchChange } = useFilteredData(items, 'message');

  useEffect(() => {
    dispatch(setContextualSupport(SUPPORT_KEY_NOTIFICATION));

    dispatch(
      loadAllNotifications(
        notificationsTypes.ALL,
        BATCH_SIZE,
        null,
        Number.parseInt(params.page) || 1,
      ),
    );
    updateUrl({ page: params.page || 1 });

    return function cleanup() {
      dispatch(unsetContextualSupport(SUPPORT_KEY_NOTIFICATION));
    };
  }, []);

  const loadMore = useCallback(
    (page: number) => {
      if (page) {
        updateUrl({ page });
        dispatch(loadAllNotifications(notificationsTypes.ALL, BATCH_SIZE, null, page));
      }
    },
    [meta],
  );

  const onClickNotificationsSettings = () => {
    history.push(NotificationSettings.screenConfig.path as string);
  };

  return (
    <div className={styles.container}>
      <BannerDescription title="Notifications" description={description} />
      <div ref={headerRef} className={styles.header}>
        <TextField
          sx={{
            minWidth: 450,
          }}
          onChange={onSearchChange}
          InputProps={{
            startAdornment: <SearchRounded />,
          }}
          size="small"
          placeholder="Search for IDs, Name's or Notification Type"
        />
        <Button onClick={onClickNotificationsSettings} variant="text" endIcon={<ArrowSmallRight />}>
          Notification Settings
        </Button>
      </div>
      <NotificationsListView
        items={notifications}
        loading={loading}
        pageCount={meta.totalPages}
        currentPage={meta.currentPage}
        onPageChange={loadMore}
      />
    </div>
  );
};

Notifications.screenConfig = {
  screenName: 'Notifications',
  domain: 'monitor',
  path: `${ROUTE_REDESIGN}${ROUTE_MONITOR}/notifications`,
};
